import { QueryClient } from '@tanstack/react-query';
import { logger } from '../utils/logger';
import { createAppError, ErrorType, getErrorType, isRetryableError } from '../utils/queryErrorUtils';

/**
 * Global error handler for query and mutation errors
 * 
 * @param error Error received from a query or mutation
 * @param source Source of the error (query or mutation)
 * @param context Additional context about where the error occurred
 */
function handleGlobalQueryError(
  error: unknown, 
  source: 'query' | 'mutation' | 'cache',
  context?: Record<string, unknown>
) {
  // Create a standardized error object
  const appError = createAppError(error, { 
    source,
    ...context 
  });

  // Add custom handling based on error type if needed
  // This is just for additional handling - actual error is already logged
  // in createAppError
  switch(appError.type) {
    case ErrorType.AUTH_EXPIRED:
    case ErrorType.AUTH_REQUIRED:
      // Auth errors are handled by useQueryErrorHandler in the components
      logger.error('Authentication error in global handler', { 
        type: appError.type,
        source
      });
      break;
    
    case ErrorType.NETWORK:
    case ErrorType.OFFLINE:
      // These are handled by ConnectionStateContext
      logger.connectivity('Network/offline error in global handler', { 
        type: appError.type,
        source
      });
      break;
      
    default:
      // Just add source context for easier debugging
      logger.error(`${source} error in global handler`, { 
        type: appError.type,
        message: appError.message,
        source
      });
      break;
  }

  return appError;
}

/**
 * Default retry function for React Query
 * Only retry errors that are considered retryable
 * 
 * @param failureCount Number of previous retry attempts
 * @param error The error that occurred
 * @returns Boolean indicating whether to retry
 */
function defaultRetryFunction(failureCount: number, error: unknown): boolean {
  // Max 3 retries
  if (failureCount >= 3) return false;
  
  // Get error type
  const errorType = getErrorType(error);
  
  // Only retry certain error types
  return isRetryableError(errorType);
}

/**
 * Create and configure the React Query client with optimal settings for Zentime
 * 
 * Key concepts:
 * - staleTime: How long data is considered fresh (won't refetch)
 * - gcTime: How long inactive data remains in cache before garbage collection
 * - refetch behaviors: When to automatically refresh data
 * 
 * IMPORTANT: Note that this queryClient instance is currently NOT used in the app.
 * There is a duplicate QueryClient instantiation in ReactQueryProvider.tsx which is the
 * primary one actually used in the application. Future refactoring should consolidate
 * these instances, but requires careful testing.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh for 5 minutes
      gcTime: 24 * 60 * 60 * 1000, // 24 hours - inactive cache data expires after 24 hours
      refetchOnWindowFocus: true, // Refetch when window regains focus
      refetchOnMount: true, // Refetch when component mounts if data is stale
      refetchOnReconnect: true, // Refetch when network reconnects
      retry: defaultRetryFunction, // Custom retry function
      onError: (error, query) => {
        // Handle query errors with context
        handleGlobalQueryError(error, 'query', {
          queryKey: query.queryKey,
          type: 'query'
        });
      }
    },
    mutations: {
      // Default options for mutations
      retry: 2, // Retry failed mutations twice
      onError: (error, variables, context) => {
        // Handle mutation errors with context
        handleGlobalQueryError(error, 'mutation', {
          variables,
          context,
          type: 'mutation'
        });
      },
    },
  },
  // Global error handler for all queries
  queryCache: {
    onError: (error) => {
      // Handle query cache errors
      handleGlobalQueryError(error, 'cache');
    },
  },
});

/**
 * IMPORTANT TECHNICAL DEBT NOTE:
 * 
 * This queryClient instance is currently NOT USED in the app. Instead, there is
 * a duplicate QueryClient instance in ReactQueryProvider.tsx which is the one actually
 * used throughout the application.
 * 
 * The functions in this file (like handleGlobalQueryError and defaultRetryFunction)
 * are also not currently being used, as the ReactQueryProvider instance uses its own
 * simpler configuration. The error handling implementations are kept here for reference.
 * 
 * Future refactoring should address this by:
 * 1. Consolidating to use a single instance
 * 2. Ensuring proper error handling is in place
 * 3. Making sure persistence (handled by PersistQueryClientProvider) is preserved
 * 
 * Implementation note: When this refactoring occurs, make sure to test:
 * - Offline/online transitions
 * - Authentication errors
 * - Query reset between different users
 */

/**
 * PARAMETER PASSING FORMAT FOR REACT QUERY MUTATION HOOKS
 * 
 * When calling mutation hooks like `useSignIn` and `useSignUp`, be careful with
 * parameter passing format. These hooks support multiple formats, and using the
 * incorrect format can lead to errors like "Email is required" even when you're
 * providing an email.
 * 
 * Correct parameter formats:
 * 
 * ```typescript
 * // Object format (RECOMMENDED)
 * await signIn({ email, password });  
 * 
 * // Array format (also supported)
 * await signIn([email, password]);
 * 
 * // INCORRECT format (will result in "Email is required" error)
 * await signIn(email, password);  // Don't do this
 * ```
 * 
 * The mutation hooks are defined with overloaded function types that expect
 * either an object or an array. Passing parameters directly will cause TypeScript
 * to treat the first parameter as the entire input, leading to validation failures.
 * 
 * Best practice:
 * - Always use the object format for clarity and type safety
 * - This provides better type checking and is less prone to parameter order mistakes
 */